/**
 * Theme management for Imago TMA.
 *
 * Telegram injects --tg-theme-* as inline styles on <html>, overriding
 * any :root CSS rules. We override them back and watch for re-injection
 * using MutationObserver (fires when app is backgrounded/foregrounded).
 *
 * Light theme is the default. Dark theme toggle will be added later.
 */

const LIGHT_THEME: Record<string, string> = {
  '--tg-theme-bg-color': '#ffffff',
  '--tg-theme-secondary-bg-color': '#f2f2f7',
  '--tg-theme-text-color': '#000000',
  '--tg-theme-hint-color': '#8e8e93',
  '--tg-theme-link-color': '#007aff',
  '--tg-theme-button-color': '#007aff',
  '--tg-theme-button-text-color': '#ffffff',
  '--tg-theme-accent-text-color': '#007aff',
  '--tg-theme-header-bg-color': '#f8f8f8',
  '--tg-theme-bottom-bar-bg-color': '#f8f8f8',
  '--tg-theme-section-bg-color': '#ffffff',
  '--tg-theme-section-separator-color': '#c6c6c8',
  '--tg-theme-subtitle-text-color': '#8e8e93',
  '--tg-theme-destructive-text-color': '#ff3b30',
}

function apply(): void {
  const root = document.documentElement
  Object.entries(LIGHT_THEME).forEach(([key, value]) => {
    root.style.setProperty(key, value)
  })

  const bg = LIGHT_THEME['--tg-theme-bg-color']!
  const text = LIGHT_THEME['--tg-theme-text-color']!

  document.body.style.backgroundColor = bg
  document.body.style.color = text
  // Also set html background to fix iOS overscroll rubber-band (black flash)
  root.style.backgroundColor = bg

  // Tell Telegram to paint its native header and app background to match our theme.
  // This removes the black-header / white-content mismatch visible in the TG chrome.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tg = window.Telegram?.WebApp as any
  tg?.setHeaderColor?.(bg)
  tg?.setBackgroundColor?.(bg)
}

export function applyLightTheme(): void {
  apply()

  // Watch for Telegram re-injecting theme vars (happens on foreground/resume).
  // Telegram modifies <html style="..."> → MutationObserver catches it → re-apply.
  const observer = new MutationObserver(() => {
    // Pause observer to avoid infinite loop while we write back
    observer.disconnect()
    apply()
    observe()
  })

  function observe() {
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    })
  }

  observe()

  // Also re-apply on Telegram's themeChanged event if available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window.Telegram?.WebApp as any)?.onEvent?.('themeChanged', () => {
    observer.disconnect()
    apply()
    observe()
  })
}
